import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  type GetListDialogsDialogListPostApiArg,
  useGetListDialogsDialogListPostQueryQuery,
} from '@shared/api';
import { useDebouncedFunc } from '@shared/hooks/use-debounced-func';
import type { Filter } from '@shared/ui/filters/lib/types';
import { useAppSelector } from '@shared/redux';
import { authSelectors } from '@shared/redux/ducks/auth';

interface UseGetDialogsProps {
  search?: string;
  skip?: boolean;
  pageSize?: number;
  filters?: Filter[];
  tab?: 'total' | 'leads';
}

export const useGetDialogs = ({
  search,
  skip = false,
  pageSize = 20,
  filters = [],
  tab = 'total',
}: UseGetDialogsProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState(search ?? '');
  const [isRefetching, setIsRefetching] = useState(false);

  const currentOrganization = useAppSelector(authSelectors.selectCurrentOrganizationInfo);
  const organizationId = currentOrganization?.id ?? 0;

  const updateDebouncedSearch = useDebouncedFunc(
    useCallback((val: string) => {
      setDebouncedSearch(val);
      setCurrentPage(1);
    }, []),
    300,
  );

  useEffect(() => {
    setCurrentPage(1);
    updateDebouncedSearch(search ?? '');
  }, [search, updateDebouncedSearch]);

  const { channelIds, channelIdsKey } = useMemo(() => {
    const rawChannelIds = filters
      .find((f) => f.type === 'channels')
      ?.option?.map((opt) => opt.value);
    const normalizedChannelIds = (rawChannelIds ?? [])
      .map((value) => (typeof value === 'number' ? value : Number(value)))
      .filter((value) => Number.isFinite(value)) as number[];
    const uniqueSorted = Array.from(new Set(normalizedChannelIds)).sort((a, b) => a - b);

    return {
      channelIds: uniqueSorted.length ? uniqueSorted : undefined,
      channelIdsKey: uniqueSorted.join(','),
    };
  }, [filters]);

  const listScopeKey = `${tab}:${channelIdsKey}`;
  const prevListScopeKeyRef = useRef(listScopeKey);
  const shouldUseFirstPage = prevListScopeKeyRef.current !== listScopeKey;
  const pageForQuery = shouldUseFirstPage ? 1 : currentPage;

  const queryArgs = useMemo(
    () =>
      ({
        offset: Math.max(0, (pageForQuery - 1) * pageSize),
        limit: pageSize,
        dialogListFilters: {
          organization_id: organizationId,
          channel_ids: channelIds ?? null,
          is_lead: tab === 'leads' ? true : null,
          q: debouncedSearch || undefined,
        },
      }) as GetListDialogsDialogListPostApiArg,
    [organizationId, pageForQuery, pageSize, channelIdsKey, debouncedSearch, tab],
  );

  const {
    data,
    isLoading,
    isFetching,
    isError,
    isUninitialized,
    refetch,
  } = useGetListDialogsDialogListPostQueryQuery(queryArgs, {
    skip: skip || !currentOrganization?.id,
  });

  useEffect(() => {
    if (prevListScopeKeyRef.current === listScopeKey) return;
    prevListScopeKeyRef.current = listScopeKey;
    setCurrentPage(1);
  }, [listScopeKey]);

  const dialogs = data?.items ?? [];
  const currentDialogs = dialogs.length;
  const count = data?.count ?? 0;
  const isEmpty = !dialogs.length && !isFetching && !isLoading && !isUninitialized;
  const hasMore = currentDialogs < count;
  const totalPages = Math.ceil(count / pageSize);

  const pageChange = (page: number) => setCurrentPage(page);

  const fetchNextData = () => {
    if (hasMore && !isFetching && !isLoading) {
      pageChange(currentPage + 1);
    }
  };

  const resetPagination = () => setCurrentPage(1);

  const handleRefetch = () => {
    setIsRefetching(true);
    refetch().finally(() => setIsRefetching(false));
  };

  return {
    dialogs,
    count,
    currentDialogs,
    currentPage,
    totalPages,
    hasMore,
    isEmpty,
    isLoading,
    isFetching,
    isError,
    isRefetching,
    // fetchMore,
    resetPagination,
    pageChange,
    fetchNextData,
    handleRefetch,
  };
};
